import { useCallback, useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import CustomButton from '../../shared/customButton';
import Search from '../../shared/search';
import CustomFormDrawer from '../../shared/customFormDrawer';
import Filter from '../../shared/filter';
import AlertDialog from '../../shared/alertDialog';
import SelectField from '../../shared/selectField';
import NotificationCard from '../../shared/notificationCard';
import JobItem from '../../shared/jobItem';
import {
  api_add_candidate_to_step,
  api_candidate,
  API_CANDIDATES,
  API_CANDIDATES_FROM_CV,
  api_candidates_in_job,
  api_job,
  api_job_archive,
  api_job_duplicate,
  api_job_recruitment_steps,
  api_job_team,
  api_job_unarchive,
  API_JOBS,
} from '../../../constants/apiRoutes';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  APP_PAGES,
  JOBS_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
  PARAMS,
} from '../../../constants/pages';
import { sticky } from '../../../constants/helperCssRules';
import { jobsSelector } from '../../../store/selectors/jobsSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import {
  getAllCandidates,
  getArchivedJobs,
  getJobs,
  clearAllCandidates,
  clearJobs,
} from '../../../store/modules/jobs';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import { getAllUsers, clearAllUsers } from '../../../store/modules/people';
import http from '../../../utility/http';
import { useTranslations } from '../../../utility/useTranslations';
import {
  getObjectToNumberArray,
  isArray,
  isArrayEmpty,
  isObjectEmpty,
  trimString,
} from '../../../utility/helpers';
import { hasAuthPermissionsCheck } from '../../../utility/permissions';
import {
  hasNextPage,
  parseDuplicateParameters,
  parseQueryParams,
  showErrorMessage,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import { isEmpty } from '../../../utility/validation';
import {
  prepareAddCandidatesFields,
  GET_STEPS_FIELDS,
  getCandidateActionItems,
  getCandidateFields,
  getDepartmentOptions,
  getFields,
  prepareJobActionItems,
  getRecruitmentSteps,
  getUploadCvFields,
  getViewOptions,
  prepareHiringTeamFields,
  prepareCandidateInitialData,
  prepareCandidatesInitialData,
  prepareHiringTeamInitialData,
  prepareInitialData,
  UPLOAD_CV_INITIAL_DATA,
} from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  subHeader: {
    padding: spacing(8, 0, 4),
    ...sticky(primary.white, 105),
  },
  form: {
    display: 'grid',
    gridColumnGap: 24,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridTemplateAreas: `
      "firstName lastName"
      "professionTitle email"
      "dateOfBirth phone"
      "referral ."
      "cv parseCheckbox"
      "education education"
      "experience experience"
      "skills skills"
      "city address"
      "country ."
    `,
  },
  firstName: {
    gridArea: 'firstName',
  },
  lastName: {
    gridArea: 'lastName',
  },
  email: {
    gridArea: 'email',
  },
  cv: {
    gridArea: 'cv',
  },
  parseCheckbox: {
    gridArea: 'parseCheckbox',
    display: 'flex',
    alignItems: 'center',
  },
  professionTitle: {
    gridArea: 'professionTitle',
  },
  experience: {
    gridArea: 'experience',
  },
  skills: {
    gridArea: 'skills',
  },
  education: {
    gridArea: 'education',
  },
  referral: {
    gridArea: 'referral',
  },
  dateOfBirth: {
    gridArea: 'dateOfBirth',
  },
  phone: {
    gridArea: 'phone',
  },
  address: {
    gridArea: 'address',
  },
  city: {
    gridArea: 'city',
  },
  country: {
    gridArea: 'country',
  },
  jobsPageContainer: {
    width: 'inherit',
    display: 'grid',
  },
  drawerContent: {
    height: '100%',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    marginBottom: 0,
    minHeight: 0,
  },
  picker: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  actionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 32,
  },
  subActionWrapper: {
    display: 'flex',
  },
  spacing: {
    marginLeft: spacing(2),
  },
  addJobButton: {
    marginLeft: spacing(2),
    minWidth: 'fit-content',
    '&:active': {
      animation: '300ms ease-out',
    },
  },
  search: {
    width: 301,
  },
  customLabelClass: {
    fontSize: 16,
    lineHeight: '24px',
    fontFamily: 'ProximaNova-Bold',
  },
  customLabelWrapperClass: {
    display: 'grid',
    marginLeft: spacing(2),
  },
  customOptionLabelWrapperClass: {
    '& svg': {
      width: '24px !important',
      height: '24px !important',
    },
  },
  customRadioClass: {
    paddingTop: spacing(0.5),
    paddingLeft: spacing(0.5),
    height: '100%',
    width: 'inherit',
    alignItems: 'flex-start',
  },
  loadMoreContainer: {
    margin: spacing(2, 0, 2, 0),
    display: 'flex',
    justifyContent: 'center',
  },
  loadMore: {
    border: `1px solid ${primary.bluish3}`,
    borderRadius: 44,
    color: primary.bluish4,
    lineHeight: '24px',
    justifySelf: 'center',
    padding: spacing(1, 4),
    maxHeight: 32,
  },
  actionButton: {
    marginLeft: spacing(2),
    width: 225,
  },
  customPaper: {
    maxHeight: 202,
    maxWidth: 245,
  },
}));

const JobsPage = ({
  location,
  auth,
  accessControl,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.JOBS);
  const { PAGE, PAGE_SIZE, SEARCH, DEPARTMENT, IS_ACTIVE } = PARAMS;
  const { page: defaultPage, page_size } = JOBS_DEFAULT_PARAMS;

  const [canEdit, canDelete] = hasAuthPermissionsCheck(
    [PERMISSION_CODES.RECRUITMENT_EDIT, PERMISSION_CODES.RECRUITMENT_DELETE],
    accessControl
  );
  const { jobs, allCandidates } = useSelector(jobsSelector);
  const { allUsers } = useSelector(peopleSelector);
  const { departments } = useSelector(divisionsAndDepartmentsSelector);
  const { categories } = useSelector(tagsSelector);

  const jobResults = jobs.results;
  const hasJobs = isArray(jobResults) && !isArrayEmpty(jobResults);
  const hasLoadMore = hasNextPage(jobs);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState({});

  const [hiringTeam, setHiringTeam] = useState([]);
  const [candidatesForJob, setCandidatesForJob] = useState([]);
  const [isHiringTeamDrawerOpened, setIsHiringTeamDrawerOpened] =
    useState(false);

  const [isStepsDrawerOpened, setIsStepsDrawerOpened] = useState(false);
  const departmentOptions = getDepartmentOptions(
    departments,
    translations,
    true
  );
  const [isEdit, setIsEdit] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isAddJobDrawerOpened, setIsAddJobDrawerOpened] = useState(false);
  const viewOptions = getViewOptions(translations.viewOptions);
  const [selectedView, setSelectedView] = useState(true);

  const [isArchiveJobDialogOpened, setIsArchiveJobDialogOpened] =
    useState(false);
  const [isUnarchiveJobDialogOpened, setIsUnarchiveJobDialogOpened] =
    useState(false);
  const [isDeleteJobDialogOpened, setIsDeleteJobDialogOpened] = useState(false);
  const [isDuplicateJobDialogOpened, setIsDuplicateJobDialogOpened] =
    useState(false);
  const [
    isAddManuallyCandidatesDrawerOpened,
    setIsAddManuallyCandidatesDrawerOpened,
  ] = useState(false);
  const [isUploadCvDrawerOpened, setIsUploadCvDrawerOpened] = useState(false);
  const [
    isAddExistingCandidatesDrawerOpened,
    setIsAddExistingCandidatesDrawerOpened,
  ] = useState(false);

  const handlePageChange = ({
    newPage,
    newFilters,
    newSearch,
    view,
    isLoadMore,
  } = {}) => {
    const params = {
      [PAGE]: newPage,
      [PAGE_SIZE]: page_size,
      [IS_ACTIVE]: view,
      ...newFilters,
      ...(newSearch ? { [SEARCH]: newSearch } : {}),
    };
    const query = parseDuplicateParameters(params);

    if (location.search !== `?${query}`) {
      navigate(`/jobs/?${query}`, { replace: true });
    }

    return view
      ? getJobs(dispatch, isLoadMore, params)
      : getArchivedJobs(dispatch, isLoadMore, params);
  };

  const toggleAddJobDrawer = () =>
    setIsAddJobDrawerOpened(prevIsOpened => !prevIsOpened);

  const toggleHiringTeamDrawer = () =>
    setIsHiringTeamDrawerOpened(prevIsOpened => !prevIsOpened);

  const toggleStepsDrawer = () =>
    setIsStepsDrawerOpened(prevIsOpened => !prevIsOpened);

  const toggleUploadCvDrawer = () =>
    setIsUploadCvDrawerOpened(prevIsOpened => !prevIsOpened);

  const onSearch = async text => {
    const searchTerm = trimString(text);
    try {
      setPage(defaultPage);
      setSearch(searchTerm);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: searchTerm,
        newFilters: selectedFilters,
        view: selectedView,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handlePageChange({
      newPage: page + 1,
      newSearch: search,
      newFilters: selectedFilters,
      view: selectedView,
      isLoadMore: true,
    });
  };

  const handleSaveJob = async data => {
    if (isObjectEmpty(selectedJob)) {
      await http.post(API_JOBS, data);
      showSuccessMessage(translations.successMessages.jobSaved);
      setPage(defaultPage);
      return handlePageChange({
        newPage: defaultPage,
        newFilters: selectedFilters,
        newSearch: search,
        view: selectedView,
      });
    }
    await http.patch(api_job(selectedJob.id), data);
    showSuccessMessage(translations.successMessages.jobUpdated);
    setPage(defaultPage);
    return handlePageChange({
      newPage: defaultPage,
      newFilters: selectedFilters,
      newSearch: search,
      view: selectedView,
    });
  };

  const handleDepartmentFilter = department => {
    const updatedFilters = department
      ? { ...selectedFilters, [DEPARTMENT]: department }
      : {};
    setPage(defaultPage);
    setSelectedFilters(updatedFilters);
    handlePageChange({
      newPage: defaultPage,
      newFilters: updatedFilters,
      newSearch: search,
      view: selectedView,
    });
  };

  const handleArchiveJob = job => {
    setSelectedJob(job);
    setIsArchiveJobDialogOpened(true);
  };

  const handleCloseArchive = () => {
    setSelectedJob({});
    setIsArchiveJobDialogOpened(false);
  };

  const handleConfirmArchive = async () => {
    try {
      await http.patch(api_job_archive(selectedJob.id)).then(() => {
        showSuccessMessage(translations.archiveJobDialog.archiveSuccess);
        setPage(defaultPage);
        handlePageChange({
          newPage: defaultPage,
          newFilters: selectedFilters,
          newSearch: search,
          view: selectedView,
        });
      });
    } finally {
      setIsArchiveJobDialogOpened(false);
      setSelectedJob({});
    }
  };

  const handleDeleteJob = job => {
    setSelectedJob(job);
    setIsDeleteJobDialogOpened(true);
  };

  const handleCloseDelete = () => {
    setSelectedJob({});
    setIsDeleteJobDialogOpened(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await http.delete(api_job(selectedJob.id));
      showSuccessMessage(translations.deleteJobDialog.deleteSuccess);
      setPage(defaultPage);
      await handlePageChange({
        newPage: defaultPage,
        newFilters: selectedFilters,
        newSearch: search,
        view: selectedView,
      });
    } finally {
      setIsDeleteJobDialogOpened(false);
      setSelectedJob({});
    }
  };

  const handleEditJob = job => {
    setSelectedJob(job);
    toggleAddJobDrawer();
    setIsEdit(true);
  };

  const handleCreateTeam = async (e, job) => {
    e.stopPropagation();
    const { data } = await http.get(api_job_team(job.id));
    setSelectedJob(job);
    setHiringTeam(data);
    toggleHiringTeamDrawer();
  };

  const handleCloseHiringTeamDrawer = () => {
    toggleHiringTeamDrawer();
    setHiringTeam([]);
    setSelectedJob({});
  };

  const handleSaveHiringTeam = async ({ hiring_team }) => {
    await http.patch(api_job_team(selectedJob.id), {
      hiring_team: getObjectToNumberArray(hiring_team),
    });
    showSuccessMessage(translations.successMessages.hiringTeamSaved);
    setPage(defaultPage);
    handlePageChange({
      newPage: defaultPage,
      newSearch: search,
      newFilters: selectedFilters,
      view: selectedView,
    });
  };

  const handleCloseAddJobDrawer = () => {
    toggleAddJobDrawer();
    setSelectedJob({});
    setIsEdit(false);
  };

  const handleJobClick = jobId => {
    navigate(`/jobs/${jobId}/details/0/profile/`);
  };

  const handleJobStepClick = (jobId, stepId) => {
    navigate(`/jobs/${jobId}/details/${stepId}/profile/`);
  };

  const handleDuplicateDialog = job => {
    setSelectedJob(job);
    setIsDuplicateJobDialogOpened(true);
  };

  const handleCloseDuplicateDialog = () => {
    setSelectedJob({});
    setIsDuplicateJobDialogOpened(false);
  };

  const handleDuplicateJob = async () => {
    await http.post(api_job_duplicate(selectedJob.id)).then(() => {
      showSuccessMessage(translations.successMessages.duplicateSuccess);
      setIsDuplicateJobDialogOpened(false);
      setSelectedJob({});
      setPage(defaultPage);
      handlePageChange({
        newPage: defaultPage,
        newFilters: selectedFilters,
        newSearch: search,
        view: selectedView,
      });
    });
  };

  const handleUnarchiveJob = (e, job) => {
    e.stopPropagation();
    setSelectedJob(job);
    setIsUnarchiveJobDialogOpened(true);
  };

  const handleCloseUnarchive = () => {
    setSelectedJob({});
    setIsUnarchiveJobDialogOpened(false);
  };

  const handleConfirmUnarchive = async () => {
    try {
      await http.patch(api_job_unarchive(selectedJob.id)).then(() => {
        let jobView = selectedView;
        if (jobResults.length === 1) {
          setSelectedView(viewOptions[0].value);
          jobView = viewOptions[0].value;
        }
        showSuccessMessage(translations.unarchiveJobDialog.unarchiveSuccess);
        setPage(defaultPage);
        handlePageChange({
          newPage: defaultPage,
          newSearch: search,
          newFilters: selectedFilters,
          view: jobView,
        });
      });
    } finally {
      setIsUnarchiveJobDialogOpened(false);
      setSelectedJob({});
    }
  };

  const handleAddCandidateManually = job => {
    setSelectedJob(job);
    setIsAddManuallyCandidatesDrawerOpened(true);
  };

  const handleSteps = (e, job) => {
    e.stopPropagation();
    setSelectedJob(job);
    toggleStepsDrawer();
  };

  const handleCloseStepsDrawer = () => {
    setSelectedJob({});
    toggleStepsDrawer();
  };

  const handleAddExistingCandidates = async job => {
    const { data } = await http.get(api_candidates_in_job(job.id));
    setSelectedJob(job);
    setCandidatesForJob(data);
    setIsAddExistingCandidatesDrawerOpened(true);
  };

  const handleAddExistingCandidatesCloseDrawer = () => {
    setIsAddExistingCandidatesDrawerOpened(false);
    setCandidatesForJob([]);
    setSelectedJob({});
  };

  const handleAddExistingCandidatesSave = async ({ candidates }) => {
    await http.patch(api_candidates_in_job(selectedJob.id), {
      candidates: getObjectToNumberArray(candidates),
    });
    showSuccessMessage(translations.successMessages.existingCandidatesSaved);
    setPage(defaultPage);
    handlePageChange({
      newPage: defaultPage,
      newSearch: search,
      newFilters: selectedFilters,
      view: selectedView,
    });
  };

  const handleSaveSteps = async updatedSteps => {
    const data = updatedSteps?.recruitment_steps?.map((updatedStep, index) => ({
      ...updatedStep,
      job: selectedJob.id,
      order: index + 1,
    }));
    await http
      .patch(api_job_recruitment_steps(selectedJob.id), data)
      .then(() => {
        showSuccessMessage(translations.successMessages.stepSelectionSaved);
        setPage(defaultPage);
        handlePageChange({
          newPage: defaultPage,
          newFilters: selectedFilters,
          newSearch: search,
          view: selectedView,
        });
      });
  };

  const handleSaveCandidateManually = async candidate => {
    const initialStep = selectedJob.steps.find(step => step.order === 1);
    const { cv, parse_cv_data, ...candidateData } = candidate;
    const formData = new FormData();
    formData.append('cv', cv || '');
    formData.append('parse_cv_data', parse_cv_data || false);

    await http.post(API_CANDIDATES, candidateData).then(candidateInfo => {
      http
        .patch(api_candidate(candidateInfo.data.id), formData)
        .then(({ data }) => {
          http
            .patch(api_add_candidate_to_step(initialStep.id, data.id))
            .then(() => {
              showSuccessMessage(translations.successMessages.candidateSaved);
              getAllCandidates(dispatch);
              setPage(defaultPage);
              handlePageChange({
                newPage: defaultPage,
                newFilters: selectedFilters,
                newSearch: search,
                view: selectedView,
              });
            });
        });
    });
  };

  const handleCloseAddCandidatesManuallyDrawer = () => {
    setSelectedJob({});
    setIsAddManuallyCandidatesDrawerOpened(false);
  };

  const handleChangeView = async view => {
    try {
      setIsLoading(true);
      setPage(defaultPage);
      setSelectedView(view);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newFilters: selectedFilters,
        view,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadCv = job => {
    setSelectedJob(job);
    toggleUploadCvDrawer();
  };

  const handleSaveUploadCv = async data => {
    try {
      const formData = new FormData();
      formData.append('cv', data.cv || '');
      formData.append('first_name', data.first_name || '');
      formData.append('last_name', data.last_name || '');
      formData.append('selection_step', selectedJob.steps?.[0]?.id);

      return http.post(API_CANDIDATES_FROM_CV, formData).then(() => {
        showSuccessMessage(translations.cvUploadedSuccessfullyMessage);
        setSelectedJob({});
        setPage(defaultPage);
        handlePageChange({
          newPage: defaultPage,
          newFilters: selectedFilters,
          newSearch: search,
          view: selectedView,
        });
      });
    } catch {
      showErrorMessage(translations.uploadCvErrorMessage);
    }
  };

  const getNotificationMessage = () => {
    return isEmpty(search) || !isObjectEmpty(selectedFilters)
      ? translations.noSearchResultsMessage
      : translations.noResults;
  };

  const onGoToJobDetails = (e, jobId) => {
    e.stopPropagation();
    navigate(`/jobs/${jobId}/posting`);
  };

  const getInitialData = async () => {
    try {
      const params = parseQueryParams(
        location.search,
        PAGE_WHITELISTED_PARAMS.JOBS
      );
      if (!isObjectEmpty(params)) {
        const initialSearch = params[SEARCH]?.toString() || '';
        const initialFilters = {
          ...(params[DEPARTMENT] ? { [DEPARTMENT]: params[DEPARTMENT] } : {}),
        };
        const initialView = JSON.parse(params[IS_ACTIVE]);

        setSearch(initialSearch);
        setSelectedFilters(initialFilters);
        setSelectedView(initialView);
        await Promise.all([
          handlePageChange({
            newPage: page,
            newFilters: initialFilters,
            newSearch: initialSearch,
            view: initialView,
          }),
          getDivisionsAndDepartments(dispatch),
          getTagsCategories(dispatch),
          ...(canEdit
            ? [getAllCandidates(dispatch), getAllUsers(dispatch)]
            : []),
        ]);
      } else {
        await Promise.all([
          handlePageChange({
            newPage: page,
            newFilters: selectedFilters,
            newSearch: search,
            view: selectedView,
          }),
          getDivisionsAndDepartments(dispatch),
          getTagsCategories(dispatch),
          ...(canEdit
            ? [getAllCandidates(dispatch), getAllUsers(dispatch)]
            : []),
        ]);
      }
    } finally {
      setIsInitialLoad(false);
    }
  };

  const cleanup = useCallback(() => {
    dispatch(clearJobs());
    dispatch(clearDivisionsAndDepartments());
    dispatch(clearTagsCategories());
    dispatch(clearAllCandidates());
    dispatch(clearAllUsers());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return (
    <PageContainer
      {...rest}
      auth={auth}
      accessControl={accessControl}
      location={location}
      translations={translations}
      navigate={navigate}
      dispatch={dispatch}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.subHeader}>
            <div className={classes.actionWrapper}>
              <div className={classes.subActionWrapper}>
                <Search
                  className={classes.search}
                  placeholder={translations.search}
                  value={search}
                  onChange={onSearch}
                />
                <Filter
                  className={classes.spacing}
                  customPaperClass={classes.customPaper}
                  name={translations.department}
                  options={departmentOptions}
                  value={selectedFilters[DEPARTMENT]}
                  onChange={handleDepartmentFilter}
                />
              </div>
              <div className={classes.subActionWrapper}>
                <SelectField
                  className={classes.actionButton}
                  value={selectedView}
                  options={viewOptions}
                  parser={{ value: 'value', label: 'name' }}
                  onChange={handleChangeView}
                  isSearchDisabled
                  shouldRemoveLabel
                />
                {canEdit && (
                  <CustomButton
                    className={classes.addJobButton}
                    type="addRoundedNew"
                    onClick={toggleAddJobDrawer}
                  >
                    {translations.createNewButtonLabel}
                  </CustomButton>
                )}
              </div>
            </div>
          </div>
          {!isLoading && hasJobs && (
            <div className={classes.jobsPageContainer}>
              {jobResults.map(job => (
                <JobItem
                  key={`job_${job.id}`}
                  translations={translations}
                  job={job}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  isArchiveView={!selectedView}
                  actions={prepareJobActionItems(
                    translations.menuItemLabels,
                    job,
                    canEdit,
                    canDelete,
                    handleEditJob,
                    handleDuplicateDialog,
                    handleArchiveJob,
                    handleDeleteJob
                  )}
                  candidatesActions={getCandidateActionItems(
                    translations.candidateMenuItemLabels,
                    job,
                    handleAddExistingCandidates,
                    handleAddCandidateManually,
                    handleUploadCv
                  )}
                  onTeamCreate={e => handleCreateTeam(e, job)}
                  onSteps={e => handleSteps(e, job)}
                  onDuplicateJob={e => {
                    e.stopPropagation();
                    handleDuplicateDialog(job);
                  }}
                  onUnarchiveJob={e => handleUnarchiveJob(e, job)}
                  onDeleteJob={e => {
                    e.stopPropagation();
                    handleDeleteJob(job);
                  }}
                  onJobClick={() => handleJobClick(job.id)}
                  onJobStepClick={stepId => handleJobStepClick(job.id, stepId)}
                  onGoToJobDetails={e => onGoToJobDetails(e, job.id)}
                />
              ))}
              {hasLoadMore && (
                <div className={classes.loadMoreContainer}>
                  <CustomButton
                    className={classes.loadMore}
                    type="addWithTextRounded"
                    onClick={onLoadMore}
                  >
                    {translations.loadMore}
                  </CustomButton>
                </div>
              )}
            </div>
          )}
          {!isLoading && !hasJobs && (
            <NotificationCard content={getNotificationMessage()} />
          )}
          <CustomFormDrawer
            translations={translations.manageJob}
            isOpened={isAddJobDrawerOpened}
            initialData={prepareInitialData(selectedJob)}
            fields={getFields(translations, classes)}
            departments={departments}
            categories={categories}
            onClose={handleCloseAddJobDrawer}
            onSave={handleSaveJob}
            isInitialValid={isEdit}
            hideDelete
            hasCancelButton
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            translations={translations.manageHiringTeam}
            isOpened={isHiringTeamDrawerOpened}
            initialData={prepareHiringTeamInitialData(hiringTeam)}
            fields={prepareHiringTeamFields(classes)}
            allUsers={allUsers}
            onClose={handleCloseHiringTeamDrawer}
            onSave={handleSaveHiringTeam}
            isInitialValid
            hideDelete
            hasCancelButton
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            translations={translations.manageAddExistingCandidates}
            isOpened={isAddExistingCandidatesDrawerOpened}
            initialData={prepareCandidatesInitialData(candidatesForJob)}
            fields={prepareAddCandidatesFields(classes)}
            allCandidates={allCandidates}
            onClose={handleAddExistingCandidatesCloseDrawer}
            onSave={handleAddExistingCandidatesSave}
            hideDelete
            hasCancelButton
            isInitialValid
          />
          <CustomFormDrawer
            customFormClass={classes.form}
            translations={translations.addCandidatesManually}
            isOpened={isAddManuallyCandidatesDrawerOpened}
            initialData={prepareCandidateInitialData()}
            fields={getCandidateFields(classes)}
            onClose={handleCloseAddCandidatesManuallyDrawer}
            onSave={handleSaveCandidateManually}
            hideDelete
            hasCancelButton
            isMiddleWidth
          />
          <AlertDialog
            translations={translations.archiveJobDialog}
            isOpened={isArchiveJobDialogOpened}
            onClose={handleCloseArchive}
            onConfirm={handleConfirmArchive}
            isWarning
          />
          <AlertDialog
            translations={translations.unarchiveJobDialog}
            isOpened={isUnarchiveJobDialogOpened}
            onClose={handleCloseUnarchive}
            onConfirm={handleConfirmUnarchive}
            isWarning
          />
          <AlertDialog
            translations={translations.deleteJobDialog}
            isOpened={isDeleteJobDialogOpened}
            onClose={handleCloseDelete}
            onConfirm={handleConfirmDelete}
            isWarning
          />
          <AlertDialog
            translations={translations.duplicateJobDialog}
            isOpened={isDuplicateJobDialogOpened}
            onClose={handleCloseDuplicateDialog}
            onConfirm={handleDuplicateJob}
          />
          <CustomFormDrawer
            translations={translations.stepsDialog}
            isOpened={isStepsDrawerOpened}
            initialData={getRecruitmentSteps(selectedJob.id, selectedJob.steps)}
            fields={GET_STEPS_FIELDS}
            onClose={handleCloseStepsDrawer}
            onSave={handleSaveSteps}
            isInitialValid={
              selectedJob.steps && !isArrayEmpty(selectedJob.steps)
            }
            hideDelete
            hasCancelButton
          />
          <CustomFormDrawer
            translations={translations.addCandidatesUsingCv}
            isOpened={isUploadCvDrawerOpened}
            initialData={UPLOAD_CV_INITIAL_DATA}
            fields={getUploadCvFields()}
            onClose={toggleUploadCvDrawer}
            onSave={handleSaveUploadCv}
            hideDelete
            hasCancelButton
          />
        </div>
      )}
    </PageContainer>
  );
};

JobsPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(JobsPage);
